import { Alert, Button, Flex, IconAddLine, View } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AcademicYearService } from "../../../services/AcademicYearService";
import { TermsContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import AcademicYearCard from "./AcademicYearCard";
import AcademicYearModal from "./AcademicYearModal";

const AcademicYears = () => {
  const { t } = useTranslation();
  const { academicYears } = useContext(TermsContext);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState(true);

  useEffect(() => {
    if (academicYears) {
      setLoadingAcademicYears(false);
    }
  }, [academicYears]);

  return (
    <>
      <View as="div" textAlign="end" margin="0 0 small 0">
        <AddAcademicYearButton />
      </View>
      {loadingAcademicYears ? (
        <Skeleton count={3} height={118} style={{ marginBottom: 15 }} />
      ) : (
        <Flex wrap="wrap" alignItems="start" gap="small">
          {academicYears.length > 0 ? (
            academicYears
              .sort((a, b) => new Date(b.start_at) - new Date(a.start_at))
              .map((term) => (
                <Flex.Item size="100%" key={term.id}>
                  <AcademicYearCard academicYear={term} />
                </Flex.Item>
              ))
          ) : (
            <Flex.Item shouldGrow>
              <Alert>{t("academicYears.noYearsAlert")}</Alert>
            </Flex.Item>
          )}
        </Flex>
      )}
    </>
  );
};

const AddAcademicYearButton = () => {
  const { t } = useTranslation();
  const { academicYears, setAcademicYears } = useContext(TermsContext);
  const showNotification = useNotification();
  const [openModal, setOpenModal] = useState(false);

  const handleCreateAcademicYear = async (
    name,
    startDate,
    endDate,
    includedInsightsTerms
  ) => {
    try {
      const newAcademicYear = await AcademicYearService.createAcademicYear({
        name: name,
        start_at: startDate,
        end_at: endDate,
        insightsTerms: includedInsightsTerms,
      });
      const newAcademicYears = [...academicYears, newAcademicYear];
      setAcademicYears(newAcademicYears);
      setOpenModal(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("academicYears.academicYear"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("academicYears.academicYear"),
        }),
        ERROR
      );
    }
  };

  return (
    <>
      <Button
        color="primary"
        renderIcon={IconAddLine}
        onClick={() => setOpenModal(true)}
      >
        {t("academicYears.addYear")}
      </Button>
      <AcademicYearModal
        open={openModal}
        setOpen={setOpenModal}
        handleSave={handleCreateAcademicYear}
      />
    </>
  );
};

export default AcademicYears;
